import React, { useState, useEffect, useRef } from "react";
import Highlighter from "react-highlight-words";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";
import Draggable from "react-draggable";

import Portal from "../../common/components/Portal";
import ChangeConfirmationModal from "../../common/components/ChangeConfirmationModal";
import dashboardActions from "../actions";

const DEFAULT_USER_SETTINGS = {
    is_favorite: false,
    remember_on_reset: false,
    remember_on_logout: false
};

const DEFAULT_COLUMN_FILTERS = {
    is_favorite: "",
    remember_on_reset: "",
    remember_on_logout: ""
};

const SETTING_COLUMNS = [
    {
        key: "is_favorite",
        label: "Favorite",
        selectAllTitle: "Mark as favorite for all assigned users",
        rowTipChecked: "Remove from this user's favorites",
        rowTipUnchecked: filterType => `Add this ${filterType} to the user's favorites`
    },
    {
        key: "remember_on_reset",
        label: "On Reset",
        selectAllTitle: "Apply on dashboard reset for all assigned users",
        rowTipChecked: "Remove default apply on dashboard reset for this user",
        rowTipUnchecked: "Apply this filter by default on dashboard reset for this user"
    },
    {
        key: "remember_on_logout",
        label: "On Login",
        selectAllTitle: "Apply on initial load and after login for all assigned users",
        rowTipChecked: "Remove default apply on login for this user",
        rowTipUnchecked: "Apply this filter by default on initial load and after login for this user"
    }
];

const AssignQueryFilterToUsersModal = ({ onCancel, clientId, onAssignUsers, selectedQueryId, isSavedViews = false }) => {
    const dispatch = useDispatch();
    const { getUserByClientQueryData } = useSelector(state => state.dashboardReducer);
    const [assigned, setAssigned] = useState([]);
    const [initialAssigned, setInitialAssigned] = useState([]);
    const [available, setAvailable] = useState([]);
    const [itemIds, setItemIds] = useState([]);
    const [userSettingsById, setUserSettingsById] = useState({});
    const [initialUserSettingsById, setInitialUserSettingsById] = useState({});
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [availableSearchKey, setAvailableSearchKey] = useState("");
    const [assignedSearchKey, setAssignedSearchKey] = useState("");
    const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
    const [roleFilter, setRoleFilter] = useState({ available: "", assigned: "" });
    const [assignedColumnFilters, setAssignedColumnFilters] = useState({ ...DEFAULT_COLUMN_FILTERS });
    const selectAllRefs = useRef({});

    const settingColumns = isSavedViews ? SETTING_COLUMNS.filter(column => column.key === "is_favorite") : SETTING_COLUMNS;
    const filterType = isSavedViews ? "view" : "filter";
    const modalTitle = `Assign Saved ${isSavedViews ? "View" : "Filter"} To Users`;

    useEffect(() => {
        dispatch(
            dashboardActions.getUsersByClientQuery({
                client_id: clientId,
                query_filter_id: selectedQueryId
            })
        );
        return () => {
            dispatch(dashboardActions.clearUserByClientQueryData());
        };
    }, [clientId, dispatch, selectedQueryId]);

    useEffect(() => {
        if (!getUserByClientQueryData?.data?.length) {
            return;
        }

        const tempAvailable = getUserByClientQueryData.data.filter(user => !user?.favourite_query_id);
        const tempAssigned = getUserByClientQueryData.data.filter(user => user?.favourite_query_id);
        const settings = tempAssigned.reduce((acc, user) => {
            acc[user.id] = {
                is_favorite: user.is_favorite ?? false,
                remember_on_reset: user.remember_on_reset ?? false,
                remember_on_logout: user.remember_on_logout ?? false
            };
            return acc;
        }, {});

        setAvailable([...tempAvailable]);
        setAssigned([...tempAssigned]);
        setItemIds(tempAssigned.map(item => item.id));
        setInitialAssigned(tempAssigned.map(item => item.id));
        setUserSettingsById(settings);
        setInitialUserSettingsById(_.cloneDeep(settings));
        setAssignedColumnFilters({ ...DEFAULT_COLUMN_FILTERS });
    }, [getUserByClientQueryData]);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [assigned, userSettingsById, isSavedViews]);

    useEffect(() => {
        settingColumns.forEach(column => {
            const input = selectAllRefs.current[column.key];
            if (!input) {
                return;
            }
            const visibleIds = assigned.map(item => item.id);
            const allChecked = visibleIds.length > 0 && visibleIds.every(id => userSettingsById[id]?.[column.key]);
            const someChecked = visibleIds.some(id => userSettingsById[id]?.[column.key]);
            input.indeterminate = someChecked && !allChecked;
        });
    }, [userSettingsById, assigned, isSavedViews]);

    const hasAssignmentChanges = () => !_.isEqual([...initialAssigned].sort(), [...itemIds].sort());

    const hasSettingChanges = () =>
        itemIds.some(id => {
            const current = userSettingsById[id] || DEFAULT_USER_SETTINGS;
            const initial = initialUserSettingsById[id] || DEFAULT_USER_SETTINGS;
            return settingColumns.some(column => !!current[column.key] !== !!initial[column.key]);
        });

    const hasChanges = () => hasAssignmentChanges() || hasSettingChanges();

    const hasActiveColumnFilters = (columnFilters = assignedColumnFilters) =>
        settingColumns.some(column => columnFilters[column.key] === "yes" || columnFilters[column.key] === "no");

    const searchInAvailable = (searchKey, roleFilterValue, assignedIds = itemIds) => {
        const availableItems = getUserByClientQueryData?.data || [];
        let result = availableItems.filter(item => !assignedIds.includes(item.id));

        if (searchKey.trim().length) {
            result = result.filter(({ name }) => name && name.toLowerCase().includes(searchKey.toLowerCase()));
        }
        if (roleFilterValue) {
            result = result.filter(({ role_name = null }) => role_name === roleFilterValue);
        }

        setAvailableSearchKey(searchKey);
        setAvailable(result);
    };

    const applyAssignedFilters = (users, searchKey, roleFilterValue, columnFilters, settingsById) => {
        let result = [...users];

        if (searchKey.trim().length) {
            result = result.filter(({ name }) => name && name.toLowerCase().includes(searchKey.toLowerCase()));
        }
        if (roleFilterValue) {
            result = result.filter(({ role_name = null }) => role_name === roleFilterValue);
        }

        settingColumns.forEach(column => {
            const filterValue = columnFilters[column.key];
            if (filterValue === "yes") {
                result = result.filter(item => !!settingsById[item.id]?.[column.key]);
            } else if (filterValue === "no") {
                result = result.filter(item => !settingsById[item.id]?.[column.key]);
            }
        });

        return result;
    };

    const searchInAssigned = (
        searchKey,
        roleFilterValue,
        columnFilters = assignedColumnFilters,
        assignedIds = itemIds,
        settingsById = userSettingsById
    ) => {
        const assignedItems = getUserByClientQueryData?.data || [];
        let result = assignedItems.filter(item => assignedIds.includes(item.id));
        result = applyAssignedFilters(result, searchKey, roleFilterValue, columnFilters, settingsById);

        setAssignedSearchKey(searchKey);
        setAssigned(result);
    };

    const onAssignedColumnFilterChange = (settingKey, value) => {
        const nextColumnFilters = {
            ...assignedColumnFilters,
            [settingKey]: value
        };
        setAssignedColumnFilters(nextColumnFilters);
        searchInAssigned(assignedSearchKey, roleFilter.assigned, nextColumnFilters);
    };

    const syncUserSettings = (assignedIds, previousIds = []) => {
        setUserSettingsById(prev => {
            const next = { ...prev };
            previousIds.forEach(id => {
                if (!assignedIds.includes(id)) {
                    delete next[id];
                }
            });
            assignedIds.forEach(id => {
                if (!next[id]) {
                    next[id] = { ...DEFAULT_USER_SETTINGS };
                }
            });
            return next;
        });
    };

    const updateAssignedList = (type, id) => {
        let tempAssigned = [...assigned];
        let tempAvailable = [...available];
        const previousIds = [...itemIds];
        let nextItemIds = [...itemIds];

        if (id === "all") {
            if (type === "add") {
                const idsToAdd = tempAvailable.map(item => item.id);
                tempAssigned = _.uniqBy([...tempAssigned, ...tempAvailable], "id");
                tempAvailable = [];
                nextItemIds = _.uniq([...nextItemIds, ...idsToAdd]);
            } else {
                const idsToRemove = tempAssigned.map(item => item.id);
                tempAvailable = _.uniqBy([...tempAvailable, ...tempAssigned], "id");
                tempAssigned = [];
                nextItemIds = nextItemIds.filter(assignedId => !idsToRemove.includes(assignedId));
            }
        } else if (type === "add") {
            const itemObj = available.find(item => item.id === id);
            if (itemObj) {
                tempAssigned.push(itemObj);
                tempAvailable = tempAvailable.filter(item => item.id !== id);
                nextItemIds = _.uniq([...nextItemIds, id]);
            }
        } else {
            const itemObj = assigned.find(item => item.id === id);
            if (itemObj) {
                tempAvailable.push(itemObj);
                tempAssigned = tempAssigned.filter(item => item.id !== id);
                nextItemIds = nextItemIds.filter(assignedId => assignedId !== id);
            }
        }

        syncUserSettings(nextItemIds, previousIds);

        const nextSettingsById = { ...userSettingsById };
        previousIds.forEach(assignedId => {
            if (!nextItemIds.includes(assignedId)) {
                delete nextSettingsById[assignedId];
            }
        });
        nextItemIds.forEach(assignedId => {
            if (!nextSettingsById[assignedId]) {
                nextSettingsById[assignedId] = { ...DEFAULT_USER_SETTINGS };
            }
        });

        const knownUsers = _.uniqBy([...tempAssigned, ...tempAvailable, ...assigned, ...(getUserByClientQueryData?.data || [])], "id");
        const fullAssigned = knownUsers.filter(item => nextItemIds.includes(item.id));
        const nextAssigned = applyAssignedFilters(
            fullAssigned,
            assignedSearchKey,
            roleFilter.assigned,
            assignedColumnFilters,
            nextSettingsById
        );

        setAssigned(nextAssigned);
        setItemIds(nextItemIds);
        searchInAvailable(availableSearchKey, roleFilter.available, nextItemIds);
    };

    const refreshAssignedIfSettingFiltered = settingsById => {
        if (hasActiveColumnFilters()) {
            searchInAssigned(assignedSearchKey, roleFilter.assigned, assignedColumnFilters, itemIds, settingsById);
        }
    };

    const toggleUserSetting = (userId, settingKey) => {
        const next = {
            ...userSettingsById,
            [userId]: {
                ...(userSettingsById[userId] || DEFAULT_USER_SETTINGS),
                [settingKey]: !(userSettingsById[userId]?.[settingKey] ?? false)
            }
        };
        setUserSettingsById(next);
        refreshAssignedIfSettingFiltered(next);
    };

    const getVisibleAssignedIds = () => assigned.map(item => item.id);

    const areAllUsersSettingChecked = settingKey => {
        const visibleIds = getVisibleAssignedIds();
        return visibleIds.length > 0 && visibleIds.every(id => userSettingsById[id]?.[settingKey]);
    };

    const toggleAllUserSetting = settingKey => {
        const visibleIds = getVisibleAssignedIds();
        if (!visibleIds.length) {
            return;
        }
        const markAll = !areAllUsersSettingChecked(settingKey);
        const next = { ...userSettingsById };
        visibleIds.forEach(id => {
            next[id] = {
                ...(next[id] || DEFAULT_USER_SETTINGS),
                [settingKey]: markAll
            };
        });
        setUserSettingsById(next);
        refreshAssignedIfSettingFiltered(next);
    };

    const getRowSettingTip = (column, isChecked) => {
        if (typeof column.rowTipUnchecked === "function") {
            return isChecked ? column.rowTipChecked : column.rowTipUnchecked(filterType);
        }
        return isChecked ? column.rowTipChecked : column.rowTipUnchecked;
    };

    const renderSettingHeader = column => (
        <th key={column.key} className="assign-setting-col">
            <div className="assign-setting-header">
                <div className="assign-setting-header-top">
                    <span
                        className="assign-setting-tooltip-wrap"
                        data-tip={column.selectAllTitle}
                        data-for="assign-query-filter-settings"
                        data-place="bottom"
                    >
                        <label className="container-check assign-setting-check assign-setting-check--header mb-0">
                            <input
                                ref={element => {
                                    selectAllRefs.current[column.key] = element;
                                }}
                                type="checkbox"
                            checked={areAllUsersSettingChecked(column.key)}
                            onChange={() => toggleAllUserSetting(column.key)}
                            disabled={!assigned.length}
                            aria-label={column.selectAllTitle}
                            />
                            <span className="checkmark" />
                        </label>
                    </span>
                    <span
                        className="assign-setting-title assign-setting-tooltip-wrap"
                        data-tip={column.selectAllTitle}
                        data-for="assign-query-filter-settings"
                        data-place="bottom"
                    >
                        {column.label}
                    </span>
                </div>
                <select
                    className="form-control assign-setting-column-filter"
                    value={assignedColumnFilters[column.key] || ""}
                    onChange={e => onAssignedColumnFilterChange(column.key, e.target.value)}
                    aria-label={`Filter by ${column.label}`}
                >
                    <option value="">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                </select>
            </div>
        </th>
    );

    const renderSettingCell = (userId, column) => {
        const isChecked = userSettingsById[userId]?.[column.key] ?? false;
        return (
            <td key={column.key} className="assign-setting-col">
                <span
                    className="assign-setting-tooltip-wrap"
                    data-tip={getRowSettingTip(column, isChecked)}
                    data-for="assign-query-filter-settings"
                    data-place="bottom"
                >
                    <label className="container-check assign-setting-check mb-0">
                        <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleUserSetting(userId, column.key)}
                            aria-label={getRowSettingTip(column, isChecked)}
                        />
                        <span className="checkmark" />
                    </label>
                </span>
            </td>
        );
    };

    const onUpdateAssignedsConfirm = async () => {
        if (hasChanges()) {
            const unAssignedIds = initialAssigned.filter(id => !itemIds.includes(id));
            onAssignUsers({
                query_id: selectedQueryId,
                user_ids: itemIds.map(id => ({
                    id,
                    is_favorite: userSettingsById[id]?.is_favorite ?? false,
                    remember_on_reset: userSettingsById[id]?.remember_on_reset ?? false,
                    remember_on_logout: userSettingsById[id]?.remember_on_logout ?? false
                })),
                unassign_user_ids: unAssignedIds
            });
        }
        return true;
    };

    const updateAssigned = () => {
        if (!hasChanges()) {
            onCancel();
        } else {
            setShowConfirmation(true);
        }
    };

    const cancelModal = () => {
        if (showCancelConfirmModal) {
            setShowCancelConfirmModal(false);
            onCancel();
        } else if (hasChanges()) {
            setShowCancelConfirmModal(true);
        } else {
            onCancel();
        }
    };

    const assignedColSpan = 3 + settingColumns.length;

    return (
        <>
            <ReactTooltip
                id="assign-query-filter-settings"
                className="assign-query-filter-tooltip"
                effect="solid"
                place="bottom"
                backgroundColor="#007bff"
                offset={{ top: 8 }}
            />
            <div className="modal assigned-modal assign-query-filter-modal" role="dialog" style={{ display: "block" }} id="modalId">
                <Draggable handle=".draggable-query-assign" cancel=".close, .assign-setting-column-filter, select, input, label">
                    <div className="modal-dialog modal-dialog-centered assignModal">
                        <div className="modal-content">
                            <div className="modal-header draggable-query-assign draggable">
                                <h5 className="modal-title">{modalTitle}</h5>
                                <button type="button" className="close" aria-label="Close" onClick={cancelModal}>
                                    <span aria-hidden="true">
                                        <img src="/img/close.svg" alt="" />
                                    </span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="outer-act-build list-sec">
                                    <div className="build-tem1">
                                        <h4>Available</h4>
                                        <div className="outer-avl-bind">
                                            <div className="sr-sec search-section">
                                                <div className="sr-out">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        onChange={e => searchInAvailable(e.target.value, roleFilter.available)}
                                                        placeholder="Search users"
                                                        value={availableSearchKey}
                                                    />
                                                    <span
                                                        className="clear-btn cursor-pointer"
                                                        onClick={() =>
                                                            availableSearchKey.trim().length ? searchInAvailable("", roleFilter.available) : null
                                                        }
                                                    >
                                                        Clear
                                                    </span>
                                                </div>
                                                <div className="sort-by-fl">
                                                    <div className="fl-outs d-flex">
                                                        <div className="selectOtr">
                                                            <select
                                                                className="form-control"
                                                                value={roleFilter.available}
                                                                onChange={e =>
                                                                    setRoleFilter(prev => {
                                                                        const next = { ...prev, available: e.target.value };
                                                                        searchInAvailable(availableSearchKey, e.target.value);
                                                                        return next;
                                                                    })
                                                                }
                                                            >
                                                                <option value="">All Roles</option>
                                                                <option value="consultancy_user">Consultancy User</option>
                                                                <option value="client_user">Client User</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="table-section assign-query-filter-table-section">
                                                <table className="table table-bordered file-system-table">
                                                    <thead>
                                                        <tr>
                                                            <th className="img-sq-box">
                                                                <i
                                                                    className="fas fa-arrow-right cursor-pointer"
                                                                    onClick={() => updateAssignedList("add", "all")}
                                                                ></i>
                                                            </th>
                                                            <th className="sel-all">Name</th>
                                                            <th className="sel-all">Role</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {available.length ? (
                                                            available.map(item => (
                                                                <tr key={item.id}>
                                                                    <td className="img-sq-box">
                                                                        <i
                                                                            className="fas fa-arrow-right cursor-pointer"
                                                                            onClick={() => updateAssignedList("add", item.id)}
                                                                        ></i>
                                                                    </td>
                                                                    <td>
                                                                        <Highlighter
                                                                            searchWords={[availableSearchKey]}
                                                                            textToHighlight={item.name}
                                                                            className="highlighter"
                                                                        />
                                                                    </td>
                                                                    <td>{item.role_name}</td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan={3} className="text-center">
                                                                    No Records Found !!
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        <div className="popup-counter">Count : {available.length}</div>
                                    </div>

                                    <div className="build-tem3">
                                        <h4>Assigned</h4>
                                        <div className="outer-avl-bind">
                                            <div className="sr-sec search-section">
                                                <div className="sr-out">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        onChange={e => searchInAssigned(e.target.value, roleFilter.assigned)}
                                                        placeholder="Search users"
                                                        value={assignedSearchKey}
                                                    />
                                                    <span
                                                        className="clear-btn cursor-pointer"
                                                        onClick={() =>
                                                            assignedSearchKey.trim().length ? searchInAssigned("", roleFilter.assigned) : null
                                                        }
                                                    >
                                                        Clear
                                                    </span>
                                                </div>
                                                <div className="sort-by-fl">
                                                    <div className="fl-outs d-flex">
                                                        <div className="selectOtr">
                                                            <select
                                                                className="form-control"
                                                                value={roleFilter.assigned}
                                                                onChange={e =>
                                                                    setRoleFilter(prev => {
                                                                        const next = { ...prev, assigned: e.target.value };
                                                                        searchInAssigned(assignedSearchKey, e.target.value);
                                                                        return next;
                                                                    })
                                                                }
                                                            >
                                                                <option value="">All Roles</option>
                                                                <option value="consultancy_user">Consultancy User</option>
                                                                <option value="client_user">Client User</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="table-section assign-query-filter-table-section assign-query-filter-table-wrap">
                                                <table className="table table-bordered file-system-table assign-query-filter-table">
                                                    <thead>
                                                        <tr>
                                                            <th className="img-sq-box">
                                                                <i
                                                                    className="fas fa-arrow-left cursor-pointer"
                                                                    onClick={() => updateAssignedList("remove", "all")}
                                                                ></i>
                                                            </th>
                                                            <th className="sel-all assign-name-col">Name</th>
                                                            <th className="sel-all assign-role-col">Role</th>
                                                            {settingColumns.map(renderSettingHeader)}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {assigned.length ? (
                                                            assigned.map(item => (
                                                                <tr key={item.id}>
                                                                    <td className="img-sq-box">
                                                                        <i
                                                                            className="fas fa-arrow-left cursor-pointer"
                                                                            onClick={() => updateAssignedList("remove", item.id)}
                                                                        ></i>
                                                                    </td>
                                                                    <td className="assign-name-col">
                                                                        <Highlighter
                                                                            searchWords={[assignedSearchKey]}
                                                                            textToHighlight={item.name}
                                                                            className="highlighter"
                                                                        />
                                                                    </td>
                                                                    <td className="assign-role-col">{item.role_name}</td>
                                                                    {settingColumns.map(column => renderSettingCell(item.id, column))}
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan={assignedColSpan} className="text-center">
                                                                    No Records Found !!
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        <div className="popup-counter">Count : {assigned.length}</div>
                                    </div>
                                </div>

                                <div className="btn-sec">
                                    <div className="btn-out-1 text-right">
                                        <button type="button" className="btn btn-primary btnRgion mr-1" onClick={updateAssigned} disabled={!hasChanges()}>
                                            Update
                                        </button>
                                        <button type="button" className="btn btn-secondary btnClr" onClick={cancelModal}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Draggable>
            </div>

            {showConfirmation && (
                <Portal
                    body={
                        <ChangeConfirmationModal
                            onCancel={() => setShowConfirmation(false)}
                            onOk={onUpdateAssignedsConfirm}
                            heading="Update User Assignment?"
                            paragraph={`Are you sure you want to update user assignments and settings for this saved ${filterType}?`}
                        />
                    }
                    onCancel={() => setShowConfirmation(false)}
                />
            )}

            {showCancelConfirmModal && (
                <Portal
                    body={
                        <ChangeConfirmationModal
                            heading="Do you want to clear and lose all changes?"
                            paragraph="This action cannot be reverted, are you sure that you need to cancel?"
                            onCancel={() => setShowCancelConfirmModal(false)}
                            onOk={cancelModal}
                        />
                    }
                    onCancel={() => setShowCancelConfirmModal(false)}
                />
            )}
        </>
    );
};

export default AssignQueryFilterToUsersModal;
